// Story 8.7 (ADO-2016) — the app-wide toast host, mounted ONCE at the composition root
// (`main.tsx`) beside the upsell host, outside the route table so a toast survives
// navigation.
//
// The polite live region is always mounted and the toast is drawn INSIDE it, so assistive
// tech announces each toast as content added to an existing region (a region inserted
// together with its text is not reliably announced). The toast has no controls, so a live
// region holding it is safe, and it never takes focus. Every toast, whatever its kind, is
// placed the same way (Story 7.9: below the site header, per the designer's frames; see
// ToastHost.module.css).
//
// ADO-2262, the prototype's toast motion: a toast drops and fades in, and fades back out
// before it goes (held through its exit, `useExitTransition`, ending on its own opacity
// `transitionend`). A replacement of the same kind keeps the toast on screen and swaps only its
// copy: the old copy fades out, then the new copy, keyed on its toast's `id`, comes in. That
// new copy is new content in the region, so it is announced, a repeat of the same message
// included. A replacement of another kind, or one that arrives while the last toast fades out,
// is a fresh toast. The dismiss timer restarts with every replacement. Under reduced motion
// nothing waits: a toast goes, and copy swaps, at once.
//
// An add-to-cart toast keeps its "Qty n" in step with its cart line while it is shown
// (`ToastCartLine`): an edit of the line updates the shown copy in place (announced as an
// update of the same toast), restarts the timer, and a line edited back down to nothing the
// add put there closes the toast.

import { useCallback, useEffect, useState } from 'react';
import {
  EXIT_GRACE_MS,
  MOTION_MS,
  prefersReducedMotion,
  Toast,
  useExitTransition,
} from '../design-system';
import { useBasketStore } from '../basket/useBasketStore';
import {
  addedToCartDescription,
  TOAST_DURATION_MS,
  useToastStore,
  type ShownToast,
} from './useToastStore';
import styles from './ToastHost.module.css';

/** The toast drawn: the store's, or the one still fading out or about to have its copy swapped. */
interface DrawnToast {
  readonly toast: ShownToast | null;
  /** Bumped for a fresh toast, which remounts the toast and runs its entrance. */
  readonly shell: number;
  /** The copy came in by a swap, so it fades in. */
  readonly copyEntering: boolean;
}

const kindOf = (toast: ShownToast): string => toast.kind ?? 'default';

export function ToastHost() {
  const toast = useToastStore((s) => s.toast);
  const dismissToast = useToastStore((s) => s.dismissToast);
  const exit = useExitTransition(toast !== null, MOTION_MS.fast);
  const [drawn, setDrawn] = useState<DrawnToast>({ toast, shell: 0, copyEntering: false });
  const [seenToast, setSeenToast] = useState(toast);
  if (toast !== seenToast) {
    setSeenToast(toast);
    if (toast !== null) {
      // Nothing on screen, the last toast fading out (the store had none), or another kind.
      if (drawn.toast === null || seenToast === null || kindOf(drawn.toast) !== kindOf(toast)) {
        setDrawn({ toast, shell: drawn.shell + 1, copyEntering: false });
      } else if (prefersReducedMotion()) {
        setDrawn({ toast, shell: drawn.shell, copyEntering: false });
      }
      // Otherwise the old copy fades out first (`swapping`), then `finishSwap` draws the new.
    }
  }
  const swapping = toast !== null && drawn.toast !== null && drawn.toast.id !== toast.id;

  const finishSwap = useCallback(() => {
    const latest = useToastStore.getState().toast;
    setDrawn((current) =>
      latest === null || current.toast === null || current.toast.id === latest.id
        ? current
        : { toast: latest, shell: current.shell, copyEntering: true },
    );
  }, []);

  // The fallback for a copy fade whose `transitionend` never arrives.
  useEffect(() => {
    if (!swapping) return;
    const timer = setTimeout(finishSwap, MOTION_MS.toastSwap + EXIT_GRACE_MS);
    return () => clearTimeout(timer);
  }, [swapping, finishSwap]);

  // The live quantity of the current toast's cart line, less what it held before the add;
  // `null` for a toast with fixed text.
  const cartLine = toast?.cartLine;
  const lineQuantity = useBasketStore((s) =>
    cartLine === undefined ? null : (s.quantityBySku.get(cartLine.skuCode) ?? 0),
  );
  const liveQuantity =
    cartLine === undefined || lineQuantity === null
      ? null
      : lineQuantity - cartLine.quantityBeforeAdd;

  const id = toast?.id;
  // Each change of the live quantity restarts the timer, so the toast stays up while the
  // Shopper is still stepping.
  useEffect(() => {
    if (id === undefined) return;
    if (liveQuantity !== null && liveQuantity <= 0) {
      dismissToast(id);
      return;
    }
    const timer = setTimeout(() => dismissToast(id), TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [id, liveQuantity, dismissToast]);

  const shown = exit.present ? drawn.toast : null;
  // Only the current toast's copy follows its line; a copy fading out keeps its own text.
  const shownDescription =
    shown !== null &&
    shown.id === id &&
    shown.cartLine !== undefined &&
    liveQuantity !== null &&
    liveQuantity > 0
      ? addedToCartDescription(liveQuantity, shown.cartLine.productName)
      : shown?.description;
  return (
    <div
      className={styles.region}
      role="status"
      aria-live="polite"
      data-closing={exit.closing ? '' : undefined}
      data-copy={
        swapping ? 'leaving' : shown !== null && drawn.copyEntering ? 'entering' : undefined
      }
      onTransitionEnd={(event) => {
        if (event.propertyName !== 'opacity') return;
        const target = event.target as HTMLElement;
        if (exit.closing && target === event.currentTarget.firstElementChild) exit.finish();
        else if (swapping && target.dataset['part'] === 'copy') finishSwap();
      }}
    >
      {shown !== null && (
        <Toast
          key={drawn.shell}
          copyKey={shown.id}
          title={shown.title}
          description={shownDescription ?? shown.description}
          kind={shown.kind}
        />
      )}
    </div>
  );
}
